import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import SectionToggle from "../../../components/sectionToggle/sectionToggle";
import KBLearnMore from "../../../components/learnMore/KBLearnMore";
import LearnMore from "../../../components/learnMore/learnMore";
import { ILEARN_URLS } from "../../../constants/ilearnUrls";
import LoadingAnimation from "../../../components/loadingAnimation/loadingAnimation";
import { actionCreators } from "./redux/numberOfActiveMembersBySagas";
import LineOrTableChart from "./smartComponents/LineOrTableChart";
import PeriodType from "./smartComponents/PeriodType";
import TableChart from "./smartComponents/TableChart";
import CustomDateRanges from "./smartComponents/CustomDateRanges";
import DatePicker from "./smartComponents/DatePicker";
import { ScoreCards } from "./smartComponents/scoreCards";
import { UpdateButtons } from "./smartComponents/UpdateButtons";
import PosTable from "./smartComponents/PosTable";
import "./numberOfActiveMembersByModule.css.scss";
import MembershipMetricFilter from "./smartComponents/MembershipMetricFilter";
import MembershipTypeFilter from "./smartComponents/MembershipTypeFilter";
import GroupBy from "./smartComponents/GroupBy";
import NoData from "../../../components/noData/noData";
import PredictionChart from "./smartComponents/PredictionChart";
import PredictionToggle from "./smartComponents/PredictionToggle";

const MEMBERS_BY_LEARN_MORE = {
  status: {
    title: "Members by Status",
    description:
      "This report shows the count of all members by their current membership status for the selected date range. These insights help you monitor active, frozen, pending-cancel, and other member categories to better understand overall membership health.",
    kbUrl: ILEARN_URLS.membership.membersByStatus
  },
  clubReadyProfile: {
    title: "Members by ClubReady Profile",
    description:
      "This report shows all members filtered by their ClubReady profile type for the selected date range. These insights help you understand how different profile segments contribute to overall membership activity.",
    kbUrl: ILEARN_URLS.membership.membersByClubReadyProfile
  },
  paying: {
    title: "Members by Paying",
    description:
      "This report shows all paying members for the selected date range, grouped by their payment status. These insights help you track active revenue-generating members and monitor billing coverage across your membership base.",
    kbUrl: ILEARN_URLS.membership.membersByPaying
  },
  payingExcludingZeroSales: {
    title: "Members by Paying Excluding Zero Sales",
    description:
      "This report shows paying members while excluding those with zero sales activity, giving you a filtered view of truly revenue-active members. These insights help you better assess membership value and identify customers contributing to actual revenue.",
    kbUrl: ILEARN_URLS.membership.membersByPayingExcludingZeroSales
  },
  membershipType: {
    title: "Members by Membership Type",
    description:
      "This report shows members categorized by their membership type for the selected date range. These insights help you understand which membership tiers are most popular and how your membership mix evolves over time.",
    kbUrl: ILEARN_URLS.membership.membersByMembershipType
  }
};

const NumberOfActiveMembersByModule = (props) => {
  const dispatch = useDispatch();

  const isLoading = useSelector((state) => state.numberOfActiveMembersBy.isLoading);
  const initialRequest = useSelector((state) => state.numberOfActiveMembersBy.initialRequest);
  const isFiltersLoading = useSelector((state) => state.numberOfActiveMembersBy.isFiltersLoading);
  const updatesLoading = useSelector((state) => state.numberOfActiveMembersBy.updatesLoading);
  const showNoDataMessage = useSelector((state) => state.numberOfActiveMembersBy.showNoDataMessage);
  const showPrediction = useSelector((state) => state.numberOfActiveMembersBy.showPrediction);
  const isPredictionLoading = useSelector(
    (state) => state.numberOfActiveMembersBy.isPredictionLoading
  );

  const { showPosModal, metric } = useSelector(
    (state) => state.numberOfActiveMembersBy.metric_params
  );
  const selectedMembershipMetric = useSelector(
    (state) => state.numberOfActiveMembersBy.selectedMembershipMetric
  );

  const showPredictionToggle = ["status", "clubReadyProfile", "paying", "payingExcludingZeroSales"].includes(
    selectedMembershipMetric?.id
  );

  const isMembershipTypeView = metric === "MembersByMembershipType";

  useEffect(() => {
    if (!initialRequest) {
      dispatch(actionCreators.fetchCurrentUser(props));
    }
  }, []);

  const content = () => {
    return (
      <div>
        <div id="override-edit-selection-bar" className="edit-selections-bar">
          <div className="top-filter">
            <div className="top-filter__left">
              {showPredictionToggle && <PredictionToggle />}
              {isMembershipTypeView && (
                <>
                  <div className="select-box-no-width" style={{ maxWidth: "130px" }}>
                    <p>Membership Type</p>
                    {isFiltersLoading ? (
                      <button className="picky__input">
                        <LoadingAnimation uiLoader />{" "}
                      </button>
                    ) : (
                      <MembershipTypeFilter />
                    )}
                  </div>
                  <div className="select-box-no-width" style={{ width: "190px" }}>
                    <p>Group</p>
                    <GroupBy />
                  </div>
                </>
              )}
            </div>
            <div className="top-filter__right">
              {!showPrediction && (
                <>
                  <div className="select-box-no-width">
                    <p>Period Type</p>
                    <PeriodType />
                  </div>
                  <div className="select-box-no-width date-range-select-box">
                    <p>Date Range</p>
                    <CustomDateRanges />
                  </div>
                  <div className="date-select-box select-box-no-width multi">
                    <p>Date</p>
                    <DatePicker />
                  </div>
                  <UpdateButtons />
                </>
              )}
            </div>
          </div>
        </div>

        <div data-testid="charts-container" className="chart-root">
          {updatesLoading || isPredictionLoading ? (
            <LoadingAnimation text="Updating data..." />
          ) : showNoDataMessage ? (
            <NoData />
          ) : (
            <>
              <ScoreCards />
              {!showPrediction && (
                <>
                  <LineOrTableChart />
                  <div className="add-color">
                    <TableChart />
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {showPredictionToggle && showPrediction && !isPredictionLoading && (
          <div className="chart-root">
            <PredictionChart />
          </div>
        )}
      </div>
    );
  };

  return (
    <div id="numberOfActiveMembersByModule-root">
      <SectionToggle
        autoHeight
        height={690}
        title="Members by"
        learnMore={
          <>
            <MembershipMetricFilter />
            {selectedMembershipMetric?.id && (
              <KBLearnMore {...MEMBERS_BY_LEARN_MORE[selectedMembershipMetric.id]} />
            )}
            {showPosModal && (
              <LearnMore
                title="Packages tracked as membership"
                content={<PosTable />}
                additionalInfo
                customStyles={{
                  maxHeight: "initial"
                }}
                testId="additionalInfoButton"
              />
            )}
          </>
        }
        isLoading={isLoading}
        content={content()}
      />
    </div>
  );
};

NumberOfActiveMembersByModule.propTypes = {
  params: PropTypes.object,
  learnMore: PropTypes.object
};

export default React.memo(NumberOfActiveMembersByModule);
